import { Trans } from '@lingui/react/macro';
import { Loader2 } from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import { cn } from '@/utils/cn';

interface DropzoneProps {
  accept: Record<string, string[]>;
  multiple?: boolean;
  onDrop: (files: File[]) => void;
  disabled?: boolean;
  isLoading?: boolean;
  hint?: string;
  disabledMessage?: React.ReactNode;
  className?: string;
}

export const Dropzone = ({
  accept,
  multiple = false,
  onDrop,
  disabled,
  isLoading,
  hint,
  disabledMessage,
  className,
}: DropzoneProps): React.ReactElement => {
  const isInactive = disabled || isLoading;

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept,
    multiple,
    disabled: isInactive,
    onDrop: (acceptedFiles) => {
      if (acceptedFiles.length > 0) onDrop(acceptedFiles);
    },
  });

  return (
    <div
      {...getRootProps({ role: 'button', tabIndex: isInactive ? -1 : 0 })}
      className={cn(
        'dropzone',
        isInactive
          ? 'cursor-not-allowed border-slate-200 bg-slate-100 opacity-60'
          : isDragActive
            ? 'dropzone-active'
            : 'dropzone-idle',
        className
      )}
    >
      <input {...getInputProps()} />
      {isLoading ? (
        <Loader2 size={14} className="animate-spin text-slate-400" />
      ) : disabled ? (
        <div className="text-sm text-slate-400">
          {disabledMessage ?? <Trans>Simulation in progress — modifications disabled</Trans>}
        </div>
      ) : (
        <>
          <div className="mb-1 text-sm text-slate-600">
            <Trans>Drag & drop or click to browse</Trans>
          </div>
          {hint && <div className="text-xs text-slate-400">{hint}</div>}
        </>
      )}
    </div>
  );
};
